import { useContext, useState, useEffect } from 'react';
import { useParams, RouterContext, goTo } from 't-lib-ui/router';
import MainLayout from '../../components/MainLayout';
import AdminHeader from '../../components/admin/AdminHeader';
import PrivateFeedIcon from '../../components/PrivateFeedIcon';
import { FlowForm, FlowFormLeaveContext, ButtonBar, useFlowFormGlobal, registerKeyHandler, unregisterKeyHandler } from 't-lib-ui/structure';
import { AppState } from '../../context/general';
import { authenticatedRequest, getAdminMagicSecret } from '../../connector/auth';
import { RestAdminFeed } from '../../schema-copy/api-schema';
import FeedImageEditor from '../../components/admin/FeedImageEditor';
import { MIN_STORED_POSTS, DEFAULT_PUBLIC_MAX_STORED_POSTS, DEFAULT_PRIVATE_MAX_STORED_POSTS, STORED_POSTS_WARN_THRESHOLD } from '../../schema-copy/feed-schema';
import { nonNull } from 't-lib-ui/helpers';
import { formatCompactDate, formatFrequency } from '../../utils/formatting';
import { A, TextButton, DropDownSelector, DropDownSelectorValue, TextArea, Input, Spinner } from 't-lib-ui/widgets';
import { OpacityContainer } from 't-lib-ui/structure';
import { checkmarkIcon, closeIcon, chevronBackIcon, thumbsUpIcon, thumbsDownIcon, closeCircleIcon, editIcon, buildIcon, bugIcon, refreshIcon, searchIcon, trashIcon } from '../../components/icons';
import { sidebarLeaveHandler } from '../../components/LeftSidebarFocus';
import { usePutAdminFeed, useGetNextFeedForReview, useCheckFeedAlias, useSetFeedAlias, useDeleteFeedAlias, useReprocessFeed, useResetFeed, useGetFeedDuplicates, useDeleteAdminFeed } from '../../connector/api';
import { FetchError, sanitizeLongText } from 't-lib-ui/connector';
import FeedPreview from '../../components/FeedPreview';
import AdminSecretModal from '../../components/admin/AdminSecretModal';
import { aliasHostMismatch } from '../../utils/alias-host';
import { isHttpUrl } from '../../utils/url';
import './AdminViewFeedPage.css';

const AdminViewFeedPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const appState = useContext(AppState);
  const routerCtx = useContext(RouterContext);
  const [adminSecret, setAdminSecret] = useState<string | null>(null);
  const [feed, setFeed] = useState<RestAdminFeed | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [allDone, setAllDone] = useState(false);
  const [publicComment, setPublicComment] = useState('');
  const [privateComment, setPrivateComment] = useState('');
  const [savingPublicComment, setSavingPublicComment] = useState(false);
  const [savingPrivateComment, setSavingPrivateComment] = useState(false);
  const [publicCommentConfirmPending, setPublicCommentConfirmPending] = useState(false);
  const [isSiteUrlAlias, setIsSiteUrlAlias] = useState(false);
  const [checkingAlias, setCheckingAlias] = useState(false);
  const [settingAlias, setSettingAlias] = useState(false);
  const [feedUrl, setFeedUrl] = useState('');
  const [savingUrl, setSavingUrl] = useState(false);
  const [siteUrlInput, setSiteUrlInput] = useState('');
  const [savingSiteUrl, setSavingSiteUrl] = useState(false);
  const [urlPolling, setUrlPolling] = useState(false);
  const [urlPollComplete, setUrlPollComplete] = useState(false);

  const [reprocessing, setReprocessing] = useState(false);
  const [shortTitle, setShortTitle] = useState('');
  const [savingShortTitle, setSavingShortTitle] = useState(false);
  const [newAliasUrl, setNewAliasUrl] = useState('');
  const [addingAlias, setAddingAlias] = useState(false);
  const [deletingAlias, setDeletingAlias] = useState<string | null>(null);
  const [maxStoredPosts, setMaxStoredPosts] = useState('');
  const [savingMaxStoredPosts, setSavingMaxStoredPosts] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [resetting, setResetting] = useState(false);
  const magic = getAdminMagicSecret() || adminSecret || '';
  const putAdminFeed = usePutAdminFeed(id, magic);
  const deleteAdminFeed = useDeleteAdminFeed(id, magic);
  const getNextFeed = useGetNextFeedForReview(magic);
  const checkFeedAlias = useCheckFeedAlias(magic);
  const setFeedAlias = useSetFeedAlias(magic);
  const deleteFeedAlias = useDeleteFeedAlias(magic);
  const reprocessFeed = useReprocessFeed(id, magic);
  const resetFeed = useResetFeed(id, magic);
  const duplicatesQuery = useGetFeedDuplicates(id, magic);
  const duplicates = duplicatesQuery.data?.feeds || [];

  useFlowFormGlobal();

  useEffect(() => {
    registerKeyHandler('adminViewFeedPage', {
      'Escape': () => { goTo(routerCtx, '/a/admin/feeds'); return true; },
    }, false, 0);

    return () => unregisterKeyHandler('adminViewFeedPage');
  }, [routerCtx]);

  useEffect(() => {
    const fetchFeed = async () => {
      try {
        setLoading(true);
        setError(false);

        let result: RestAdminFeed;
        if (id == 'next') {
          // Get next feed for review
          result = await getNextFeed();
          // Navigate to the actual feed ID to prevent infinite loop
          goTo(routerCtx, `/a/admin/feed/${result.id}`, 'replace');
          return; // Don't set feed state, let the new route handle it
        } else {
          // Get specific feed by ID
          result = await authenticatedRequest<RestAdminFeed>(
            nonNull(appState.state.authData),
            'GET',
            'api',
            `/api/admin/feed/${id}`,
            false,
            { magic }
          );
        }

        setFeed(result);
        setAllDone(false);
        setPublicComment(result.downreadPublicComment || '');
        setPrivateComment(result.downreadPrivateComment || '');
        setFeedUrl(result.url || '');
        setSiteUrlInput(result.siteUrl || '');
        setShortTitle(result.shortTitle || '');
        setMaxStoredPosts(result.maxStoredPosts != null ? String(result.maxStoredPosts) : '');
        setUrlPollComplete(false);
      } catch (e) {
        console.error('Failed to load feed:', e);
        // If 404 when getting next feed, show "all done"
        if (id == 'next' && e instanceof FetchError && e.status == 404) {
          setAllDone(true);
          setLoading(false);
        } else {
          setError(true);
          setLoading(false);
        }
        return;
      }
      setLoading(false);
    };

    fetchFeed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, appState.state.authData, magic, routerCtx]);

  // Check if site URL is already an alias
  useEffect(() => {
    const checkAlias = async () => {
      if (!feed || !feed.siteUrl || feed.siteUrl == feed.url) {
        setIsSiteUrlAlias(false);
        return;
      }

      try {
        setCheckingAlias(true);
        const result = await checkFeedAlias({ feedId: feed.id, url: feed.siteUrl });
        setIsSiteUrlAlias(result.isAlias);
      } catch (e) {
        console.error('Failed to check alias:', e);
        setIsSiteUrlAlias(false);
      } finally {
        setCheckingAlias(false);
      }
    };

    checkAlias();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feed?.id, feed?.siteUrl]);

  // Feed image: select the default (favicon / feed image / custom / fallback) via the shared editor.
  const renderImageEditorRow = () => {
    if (!feed) return null;
    return (
      <tr style={{ borderBottom: '1px solid var(--gray-925)' }}>
        <td style={{ padding: '12px 16px', fontWeight: 500, verticalAlign: 'top', width: '220px', color: 'var(--gray-350)', backgroundColor: headerBg }}>Image</td>
        <td style={{ padding: '12px 16px' }}>
          <FeedImageEditor feed={feed} magic={magic} onUpdated={setFeed} />
        </td>
      </tr>
    );
  };

  const renderRow = (label: string, value: any) => {
    if (value == null || value == undefined) return null;

    return (
      <tr style={{ borderBottom: '1px solid var(--gray-925)' }}>
        <td style={{
          padding: '12px 16px',
          fontWeight: 500,
          verticalAlign: 'top',
          width: '220px',
          color: 'var(--gray-350)',
          backgroundColor: headerBg
        }}>{label}</td>
        <td style={{
          padding: '12px 16px',
          wordBreak: 'break-word',
          color: 'var(--gray-200)'
        }}>{String(value)}</td>
      </tr>
    );
  };


  const renderSiteUrlRow = () => {
    if (!feed) return null;

    const trimmed = siteUrlInput.trim();
    const validInput = isHttpUrl(trimmed);
    const hasChanged = trimmed != (feed.siteUrl || '');
    const canSetAlias = feed.siteUrl && feed.siteUrl != feed.url && !isSiteUrlAlias;

    return (
      <tr style={{ borderBottom: '1px solid var(--gray-925)' }}>
        <td style={{
          padding: '12px 16px',
          fontWeight: 500,
          verticalAlign: 'top',
          width: '220px',
          color: 'var(--gray-350)',
          backgroundColor: headerBg
        }}>Site URL</td>
        <td style={{
          padding: '12px 16px',
          wordBreak: 'break-word',
          color: 'var(--gray-200)'
        }}>
          <Input
            value={siteUrlInput}
            onInput={setSiteUrlInput}
            placeholder="https://example.com"
            layout="singleCol"
          />
          {trimmed && !validInput && (
            <div style={{ marginTop: '4px', fontSize: '0.85rem', color: 'var(--error-color)' }}>
              Must start with http:// or https://
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginTop: '8px' }}>
            {feed.siteUrl && <A href={feed.siteUrl}>{sanitizeLongText(feed.siteUrl, 100)}</A>}
            <OpacityContainer show={hasChanged} transition={true}>
              <TextButton
                label="Save Site URL"
                icon={checkmarkIcon}
                onClick={handleSaveSiteUrl}
                focussable={hasChanged ? "flow" : "normal"}
                enabled={hasChanged && validInput ? "enabled" : "disabled"}
                busy={savingSiteUrl}
                size="small"
                type="major"
              />
            </OpacityContainer>
            {!isPrivate && (
              <TextButton
                label={isSiteUrlAlias ? "Alias Set" : "Set Alias"}
                icon={checkmarkIcon}
                onClick={handleSetAlias}
                focussable="flow"
                enabled={canSetAlias ? "enabled" : "disabled"}
                busy={settingAlias || checkingAlias}
                size="small"
              />
            )}
          </div>
        </td>
      </tr>
    );
  };

  const renderRssUrlRow = () => {
    if (!feed) return null;

    const hasChanged = feedUrl != (feed.url || '');

    return (
      <tr style={{ borderBottom: '1px solid var(--gray-925)' }}>
        <td style={{
          padding: '12px 16px',
          fontWeight: 500,
          verticalAlign: 'top',
          width: '220px',
          color: 'var(--gray-350)',
          backgroundColor: headerBg
        }}>RSS URL</td>
        <td style={{
          padding: '12px 16px',
          wordBreak: 'break-word',
          color: 'var(--gray-200)'
        }}>
          <div>
            <Input
              value={feedUrl}
              onInput={setFeedUrl}
              placeholder="https://example.com/feed.xml"
              layout="singleCol"
              className="adminFeedUrlInput"
            />
            {urlPolling && (
              <div style={{ marginTop: '8px', padding: '12px', background: 'var(--gray-975)', borderRadius: '4px', border: '1px solid var(--gray-850)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <Spinner spinning={true} />
                  <span style={{ fontSize: '0.9rem', color: 'var(--gray-400)' }}>
                    Processing feed... Status: {feed.feedStatus}
                  </span>
                </div>
              </div>
            )}
            {urlPollComplete && (
              <div style={{ marginTop: '8px', padding: '12px', background: feed.feedStatus == 'ACTIVE' ? '#d4edda' : '#f8d7da', borderRadius: '4px', border: `1px solid ${feed.feedStatus == 'ACTIVE' ? '#c3e6cb' : '#f5c6cb'}` }}>
                <div style={{ marginBottom: '8px', fontSize: '0.9rem', color: feed.feedStatus == 'ACTIVE' ? '#155724' : '#721c24' }}>
                  Feed processing complete. Status: {feed.feedStatus}
                  {feed.errorType && ` (Error: ${feed.errorType})`}
                </div>
                <TextButton
                  label="Reload Feed"
                  onClick={handleReloadFeed}
                  focussable="flow"
                  type="major"
                  size="small"
                />
              </div>
            )}
            <OpacityContainer show={hasChanged && !urlPolling} transition={true}>
              <div style={{ marginTop: '8px' }}>
                <TextButton
                  label="Update URL & Reprocess"
                  icon={checkmarkIcon}
                  onClick={handleSaveUrl}
                  focussable={hasChanged ? "flow" : "normal"}
                  enabled={hasChanged ? "enabled" : "disabled"}
                  busy={savingUrl}
                  size="small"
                  type="major"
                />
              </div>
            </OpacityContainer>
          </div>
        </td>
      </tr>
    );
  };

  const renderDateRow = (label: string, dateStr?: string) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const formatted = formatCompactDate(dateStr);
    const localTime = date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    return renderRow(label, `${formatted} (${localTime})`);
  };

  const renderFeedStatusRow = () => {
    if (!feed) return null;

    // All DB statuses are now editable
    const isReadOnly = false;

    if (isReadOnly) {
      return (
        <tr style={{ borderBottom: '1px solid var(--gray-925)' }}>
          <td style={{ padding: '12px 16px', fontWeight: 500, verticalAlign: 'top', width: '220px', color: 'var(--gray-350)', backgroundColor: headerBg }}>Feed Status</td>
          <td style={{ padding: '12px 16px', wordBreak: 'break-word', color: 'var(--gray-200)', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span>{feed.feedStatus}</span>
            <TextButton
              label="Reprocess"
              icon={refreshIcon}
              onClick={handleReprocess}
              focussable="flow"
              busy={reprocessing}
              size="small"
            />
          </td>
        </tr>
      );
    }

    return (
      <tr style={{ borderBottom: '1px solid var(--gray-925)' }}>
        <td style={{
          padding: '12px 16px',
          fontWeight: 500,
          verticalAlign: 'top',
          width: '220px',
          color: 'var(--gray-350)',
          backgroundColor: headerBg
        }}>Feed Status</td>
        <td style={{
          padding: '12px 16px',
          wordBreak: 'break-word',
          color: 'var(--gray-200)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <DropDownSelector
              selections={feedStatusOptions}
              value={feed.feedStatus}
              onSelect={handleFeedStatusChange}
              focussable="flow"
            />
            {(feed.feedStatus == 'BAD' || feed.feedStatus == 'BLOCK') && (
              <TextButton
                label="Reprocess"
                icon={refreshIcon}
                onClick={handleReprocess}
                focussable="flow"
                busy={reprocessing}
                size="small"
              />
            )}
          </div>
        </td>
      </tr>
    );
  };

  const renderVerificationRow = () => {
    if (!feed) return null;

    return (
      <tr style={{ borderBottom: '1px solid var(--gray-925)' }}>
        <td style={{
          padding: '12px 16px',
          fontWeight: 500,
          verticalAlign: 'top',
          width: '220px',
          color: 'var(--gray-350)',
          backgroundColor: headerBg
        }}>Manually Verified</td>
        <td style={{
          padding: '12px 16px',
          wordBreak: 'break-word',
          color: 'var(--gray-200)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <span>{feed.manuallyVerified ? '✓ Yes' : '✗ No'}</span>
          <TextButton
            label={feed.manuallyVerified ? "Unverify" : "Verify"}
            icon={feed.manuallyVerified ? closeIcon : checkmarkIcon}
            onClick={handleVerify}
            focussable="flow"
            busy={verifying}
            size="small"
          />
        </td>
      </tr>
    );
  };

  // The reason the last fetch failed, verbatim from the sync-server: the HTTP status or exception,
  // whether it went direct or via a proxy, and the conditional validators that were attached.
  // Monospaced because it routinely contains an ETag or an HTTP date worth reading character by
  // character — a mangled one is exactly the kind of bug this row exists to expose.
  const renderLastFetchErrorRow = () => {
    if (!feed?.lastFetchError) return null;
    return (
      <tr style={{ borderBottom: '1px solid var(--gray-925)' }}>
        <td style={{ padding: '12px 16px', fontWeight: 500, verticalAlign: 'top', width: '220px', color: 'var(--gray-350)', backgroundColor: headerBg }}>Last Fetch Error</td>
        <td style={{ padding: '12px 16px', wordBreak: 'break-word', color: 'var(--error-color)', fontFamily: 'monospace', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>
          {feed.lastFetchError}
        </td>
      </tr>
    );
  };

  const renderSectionHeader = (title: string) => (
    <tr>
      <td colSpan={2} style={{
        padding: '20px 16px 12px 16px',
        fontWeight: 600,
        fontSize: '1.1em',
        color: 'var(--gray-200)',
        borderBottom: '2px solid var(--gray-850)',
        backgroundColor: isPrivate ? 'var(--gray-800)' : 'var(--surface-raised)'
      }}>{title}</td>
    </tr>
  );

  const renderCommentRow = (
    label: string,
    value: string,
    originalValue: string | undefined,
    onValueChange: (newValue: string) => void,
    onSave: () => Promise<void>,
    saving: boolean,
    confirmPending?: boolean
  ) => {
    const hasChanged = value != (originalValue || '');

    return (
      <tr style={{ borderBottom: '1px solid var(--gray-925)' }}>
        <td style={{
          padding: '12px 16px',
          fontWeight: 500,
          verticalAlign: 'top',
          width: '220px',
          color: 'var(--gray-350)',
          backgroundColor: headerBg
        }}>{label}</td>
        <td style={{
          padding: '12px 16px',
          wordBreak: 'break-word',
          color: 'var(--gray-200)'
        }}>
          <div>
            <TextArea
              value={value}
              onInput={onValueChange}
              placeholder={`Enter ${label.toLowerCase()}...`}
              rows={3}
              layout="singleCol"
              borderless={false}
              className="adminFeedCommentTextarea"
            />
            {confirmPending && (
              <div style={{
                marginTop: '8px',
                padding: '12px',
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '4px',
                color: '#856404',
                fontWeight: 600
              }}>
                ⚠️ Warning: This comment will be visible to ALL users. Click "Confirm Save" again to proceed.
              </div>
            )}
            <OpacityContainer show={hasChanged} transition={true}>
              <div style={{ marginTop: '8px' }}>
                <TextButton
                  label={confirmPending ? "Confirm Save" : "Set"}
                  icon={checkmarkIcon}
                  onClick={onSave}
                  focussable={hasChanged ? "flow" : "normal"}
                  enabled={hasChanged ? "enabled" : "disabled"}
                  busy={saving}
                  size="small"
                  type="major"
                />
              </div>
            </OpacityContainer>
          </div>
        </td>
      </tr>
    );
  };

  // Only allow manual modification of these statuses (exclude NEW and PREVALIDATED)
  const feedStatusOptions: DropDownSelectorValue[] = [
    { id: 'BLOCK', text: 'Blocked', searchTextLc: 'blocked' },
    { id: 'ACTIVE', text: 'Active', searchTextLc: 'active' },
    { id: 'BAD', text: 'Bad', searchTextLc: 'bad' },
    { id: 'UNUSED', text: 'Unused', searchTextLc: 'unused' },
    { id: 'PRIVATE', text: 'Private', searchTextLc: 'private' },
  ];

  const isPrivate = feed?.feedStatus == 'PRIVATE';
  // A valid site URL is required before a feed can be accepted or cataloged.
  const siteUrlValid = isHttpUrl(feed?.siteUrl);
  const isAdult = feed?.category == 'adult' || feed?.downreadPrivateComment?.toLowerCase().includes('adult');
  const headerBg = isPrivate ? 'var(--gray-850)' : 'var(--gray-975)';

  const handleFeedStatusChange = async (newStatus: DropDownSelectorValue) => {
    if (!feed) return;

    try {
      const updatedFeed = await putAdminFeed({
        feedStatus: newStatus.id as RestAdminFeed['feedStatus']
      });
      setFeed(updatedFeed);
    } catch (e) {
      console.error('Failed to update feed status:', e);
    }
  };

  const handleVerify = async () => {
    if (!feed) return;

    try {
      setVerifying(true);
      const updatedFeed = await putAdminFeed({ manuallyVerified: !feed.manuallyVerified });
      setFeed(updatedFeed);
    } catch (e) {
      console.error('Failed to update verification:', e);
    } finally {
      setVerifying(false);
    }
  };

  // Sets the feed's status (and marks it manually verified) without leaving the page. Bulk feed
  // processing moved to AdminProcessFeedPage, so these buttons just update the current feed in place;
  // the button matching the new status becomes disabled.
  const handleSetStatus = async (feedStatus: 'BLOCK' | 'ACTIVE' | 'BAD') => {
    if (!feed || processing) return;
    if (feedStatus == 'ACTIVE' && !isHttpUrl(feed.siteUrl)) return;

    try {
      setProcessing(true);
      const updatedFeed = await putAdminFeed({ feedStatus, manuallyVerified: true });
      setFeed(updatedFeed);
    } catch (e) {
      console.error('Failed to update feed status:', e);
    } finally {
      setProcessing(false);
    }
  };

  const handleBack = () => {
    goTo(routerCtx, '/a/admin/feeds');
  };

  const handleEditCatalog = () => {
    goTo(routerCtx, `/a/admin/catalog/${id}`);
  };

  const handleRepair = () => {
    goTo(routerCtx, `/a/admin/feed/${id}/repair`);
  };

  const handleSavePublicComment = async () => {
    if (!feed || savingPublicComment) return;

    // Require double confirmation
    if (!publicCommentConfirmPending) {
      setPublicCommentConfirmPending(true);
      // Reset confirmation after 3 seconds
      setTimeout(() => setPublicCommentConfirmPending(false), 3000);
      return;
    }

    try {
      setSavingPublicComment(true);
      const updatedFeed = await putAdminFeed({ downreadPublicComment: publicComment });
      setFeed(updatedFeed);
      setPublicCommentConfirmPending(false);
    } catch (e) {
      console.error('Failed to update public comment:', e);
    } finally {
      setSavingPublicComment(false);
    }
  };

  const handleSaveShortTitle = async () => {
    if (!feed || savingShortTitle) return;
    try {
      setSavingShortTitle(true);
      const updatedFeed = await putAdminFeed({ shortTitle });
      setFeed(updatedFeed);
      setShortTitle(updatedFeed.shortTitle || '');
    } catch (e) {
      console.error('Failed to update shortTitle:', e);
    } finally {
      setSavingShortTitle(false);
    }
  };

  const renderShortTitleRow = () => {
    if (!feed) return null;
    const hasChanged = shortTitle != (feed.shortTitle || '');
    return (
      <tr style={{ borderBottom: '1px solid var(--gray-925)' }}>
        <td style={{
          padding: '12px 16px',
          fontWeight: 500,
          verticalAlign: 'top',
          width: '220px',
          color: 'var(--gray-350)',
          backgroundColor: headerBg
        }}>Short Title</td>
        <td style={{
          padding: '12px 16px',
          wordBreak: 'break-word',
          color: 'var(--gray-200)'
        }}>
          <Input
            value={shortTitle}
            onInput={setShortTitle}
            placeholder="Short title (leave empty to clear)"
            layout="singleCol"
          />
          <OpacityContainer show={hasChanged} transition={true}>
            <div style={{ marginTop: '8px' }}>
              <TextButton
                label="Save"
                icon={checkmarkIcon}
                onClick={handleSaveShortTitle}
                focussable={hasChanged ? "flow" : "normal"}
                enabled={hasChanged ? "enabled" : "disabled"}
                busy={savingShortTitle}
                size="small"
                type="major"
              />
            </div>
          </OpacityContainer>
        </td>
      </tr>
    );
  };

  const handleSaveMaxStoredPosts = async () => {
    if (!feed || savingMaxStoredPosts) return;
    const value = parseInt(maxStoredPosts, 10);
    if (!Number.isInteger(value) || value < MIN_STORED_POSTS || value > DEFAULT_PUBLIC_MAX_STORED_POSTS) return;
    try {
      setSavingMaxStoredPosts(true);
      const updatedFeed = await putAdminFeed({ maxStoredPosts: value });
      setFeed(updatedFeed);
      setMaxStoredPosts(updatedFeed.maxStoredPosts != null ? String(updatedFeed.maxStoredPosts) : '');
    } catch (e) {
      console.error('Failed to update max stored posts:', e);
    } finally {
      setSavingMaxStoredPosts(false);
    }
  };

  const renderMaxStoredPostsRow = () => {
    if (!feed) return null;
    const effective = feed.maxStoredPosts ?? (isPrivate ? DEFAULT_PRIVATE_MAX_STORED_POSTS : DEFAULT_PUBLIC_MAX_STORED_POSTS);
    const value = parseInt(maxStoredPosts, 10);
    const validInput = Number.isInteger(value) && value >= MIN_STORED_POSTS && value <= DEFAULT_PUBLIC_MAX_STORED_POSTS;
    const hasChanged = maxStoredPosts != (feed.maxStoredPosts != null ? String(feed.maxStoredPosts) : '');
    const showWarning = feed.postCount > STORED_POSTS_WARN_THRESHOLD && effective >= DEFAULT_PUBLIC_MAX_STORED_POSTS;

    return (
      <tr style={{ borderBottom: '1px solid var(--gray-925)' }}>
        <td style={{ padding: '12px 16px', fontWeight: 500, verticalAlign: 'top', width: '220px', color: 'var(--gray-350)', backgroundColor: headerBg }}>Max Stored Posts</td>
        <td style={{ padding: '12px 16px', wordBreak: 'break-word', color: 'var(--gray-200)' }}>
          <Input
            value={maxStoredPosts}
            onInput={setMaxStoredPosts}
            placeholder={String(effective)}
            layout="singleCol"
          />
          {maxStoredPosts && !validInput && (
            <div style={{ marginTop: '4px', fontSize: '0.85rem', color: 'var(--error-color)' }}>
              Must be a whole number between {MIN_STORED_POSTS.toLocaleString()} and {DEFAULT_PUBLIC_MAX_STORED_POSTS.toLocaleString()}.
            </div>
          )}
          {showWarning && (
            <div style={{ marginTop: '8px', padding: '12px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px', color: '#856404', fontWeight: 600 }}>
              ⚠ This feed has {feed.postCount.toLocaleString()} posts but is capped at the default {DEFAULT_PUBLIC_MAX_STORED_POSTS.toLocaleString()}. Consider setting a lower limit for this high-volume feed.
            </div>
          )}
          <OpacityContainer show={hasChanged} transition={true}>
            <div style={{ marginTop: '8px' }}>
              <TextButton
                label="Save"
                icon={checkmarkIcon}
                onClick={handleSaveMaxStoredPosts}
                focussable={hasChanged ? "flow" : "normal"}
                enabled={hasChanged && validInput ? "enabled" : "disabled"}
                busy={savingMaxStoredPosts}
                size="small"
                type="major"
              />
            </div>
          </OpacityContainer>
        </td>
      </tr>
    );
  };

  const renderAddAliasRow = () => {
    if (!feed || isPrivate) return null;
    const canAdd = !!newAliasUrl.trim();
    return (
      <tr style={{ borderBottom: '1px solid var(--gray-925)' }}>
        <td style={{
          padding: '12px 16px',
          fontWeight: 500,
          verticalAlign: 'top',
          width: '220px',
          color: 'var(--gray-350)',
          backgroundColor: headerBg
        }}>Add Alias</td>
        <td style={{
          padding: '12px 16px',
          wordBreak: 'break-word',
          color: 'var(--gray-200)'
        }}>
          <Input
            value={newAliasUrl}
            onInput={setNewAliasUrl}
            placeholder="https://example.com/feed.xml"
            layout="singleCol"
          />
          <OpacityContainer show={canAdd} transition={true}>
            <div style={{ marginTop: '8px' }}>
              <TextButton
                label="Add Alias"
                icon={checkmarkIcon}
                onClick={handleAddAlias}
                focussable={canAdd ? "flow" : "normal"}
                enabled={canAdd ? "enabled" : "disabled"}
                busy={addingAlias}
                size="small"
                type="major"
              />
            </div>
          </OpacityContainer>
        </td>
      </tr>
    );
  };

  const handleSavePrivateComment = async () => {
    if (!feed || savingPrivateComment) return;

    try {
      setSavingPrivateComment(true);
      const updatedFeed = await putAdminFeed({ downreadPrivateComment: privateComment });
      setFeed(updatedFeed);
    } catch (e) {
      console.error('Failed to update private comment:', e);
    } finally {
      setSavingPrivateComment(false);
    }
  };

  // Sets an alias; if the backend reports it already belongs to another feed, asks the
  // admin to confirm an explicit overwrite (force). Returns true once an alias is set.
  const setAliasWithConfirm = async (url: string): Promise<boolean> => {
    if (!feed) return false;
    let res = await setFeedAlias({ feedId: feed.id, url });
    if (res.conflict) {
      const ok = window.confirm(
        `"${res.conflict.aliasUrl}" is already an alias of feed ${res.conflict.ownedByFeedId}.\n\n` +
        `Overwrite and point it at THIS feed instead?`);
      if (!ok) return false;
      res = await setFeedAlias({ feedId: feed.id, url, force: true });
    }
    return !res.conflict;
  };

  const handleSetAlias = async () => {
    if (!feed || !feed.siteUrl || settingAlias) return;

    try {
      setSettingAlias(true);
      if (await setAliasWithConfirm(feed.siteUrl))
        setIsSiteUrlAlias(true);
    } catch (e) {
      console.error('Failed to set alias:', e);
    } finally {
      setSettingAlias(false);
    }
  };

  const handleAddAlias = async () => {
    if (!feed || addingAlias || !newAliasUrl.trim()) return;

    // Normalize: the backend requires an http(s):// scheme to accept/normalize
    // the alias, so add one if the admin didn't include it.
    let alias = newAliasUrl.trim();
    if (!/^https?:\/\//i.test(alias)) alias = `https://${alias}`;

    try {
      setAddingAlias(true);
      if (!(await setAliasWithConfirm(alias))) return;

      // Reload feed so the new alias shows up in the list
      const result = await authenticatedRequest<RestAdminFeed>(
        nonNull(appState.state.authData),
        'GET',
        'api',
        `/api/admin/feed/${feed.id}`,
        false,
        { magic }
      );
      setFeed(result);
      setNewAliasUrl('');
    } catch (e) {
      console.error('Failed to add alias:', e);
    } finally {
      setAddingAlias(false);
    }
  };

  // Removes an alias. Pending (unpublished) aliases are removed without confirmation;
  // published aliases are authoritative for URL lookups, so we confirm before deleting.
  const handleDeleteAlias = async (alias: string, pending: boolean) => {
    if (!feed || deletingAlias) return;
    if (!pending && !window.confirm(`Remove published alias "${alias}"?\n\nURL lookups using it will stop resolving to this feed.`)) return;

    try {
      setDeletingAlias(alias);
      await deleteFeedAlias({ feedId: feed.id, alias, pending });

      // Reload feed so the alias lists reflect the change
      const result = await authenticatedRequest<RestAdminFeed>(
        nonNull(appState.state.authData),
        'GET',
        'api',
        `/api/admin/feed/${feed.id}`,
        false,
        { magic }
      );
      setFeed(result);
    } catch (e) {
      console.error('Failed to delete alias:', e);
    } finally {
      setDeletingAlias(null);
    }
  };

  // BAD (disabled) feeds, and ACTIVE feeds that nobody subscribes to, may be hard-deleted. The
  // backend still enforces the no-subscribers/no-referenced-archives rules.
  const canDeleteFeed = !!feed && (feed.feedStatus == 'BAD' || (feed.feedStatus == 'ACTIVE' && !feed.subscriberCount));

  const handleDeleteFeed = async () => {
    if (!feed || deleting) return;
    if (!canDeleteFeed) return;
    if (!window.confirm(
      `Permanently delete "${feed.title || feed.shortTitle || feed.id}"?\n\n` +
      `This removes the feed, all its stored posts/images, and its aliases. It cannot be undone.\n\n` +
      `Only allowed for feeds with no subscribers and no starred/read-later references.`)) return;

    try {
      setDeleting(true);
      await deleteAdminFeed();
      goTo(routerCtx, '/a/admin/feeds');
    } catch (e) {
      console.error('Failed to delete feed:', e);
      window.alert(e instanceof FetchError && e.message ? `Could not delete feed: ${e.message}` : 'Could not delete feed.');
    } finally {
      setDeleting(false);
    }
  };

  // Forget what the feed has read and let it import again from scratch. Unlike Reprocess (which
  // only clears the error state and re-polls), this zeroes the post counters and the dedup memory,
  // so the very next fetch treats every item in the feed as new. The row itself — id,
  // subscriptions, catalog entry, aliases, images, admin edits — is untouched.
  const handleResetFeed = async () => {
    if (!feed || resetting) return;
    if (!window.confirm(
      `Reset "${feed.title || feed.shortTitle || feed.id}"?\n\n` +
      `Post count goes back to 0 and the feed re-imports everything it currently offers about a ` +
      `minute from now. Post numbers are reassigned from 0, so existing links to individual posts ` +
      `may point at different articles afterwards.\n\n` +
      `The feed id, its subscribers and its catalog entry are kept.`)) return;

    try {
      setResetting(true);
      const result = await resetFeed();
      await handleReloadFeed();
      window.alert(`Feed reset: ${result.originalPostCount} posts cleared. ` +
        `Re-import scheduled for ${new Date(result.nextFetchAt).toLocaleTimeString()}.`);
    } catch (e) {
      console.error('Failed to reset feed:', e);
      window.alert(e instanceof FetchError && e.message ? `Could not reset feed: ${e.message}` : 'Could not reset feed.');
    } finally {
      setResetting(false);
    }
  };

  const handleReprocess = async () => {
    if (!feed || reprocessing) return;
    try {
      setReprocessing(true);
      const updatedFeed = await reprocessFeed();
      setFeed(updatedFeed);

      // Poll for completion
      setUrlPolling(true);
      await pollFeedStatus(updatedFeed.id);
    } catch (e) {
      console.error('Failed to reprocess feed:', e);
    } finally {
      setReprocessing(false);
    }
  };

  const handleSaveUrl = async () => {
    if (!feed || savingUrl || !feedUrl.trim()) return;

    try {
      setSavingUrl(true);
      setUrlPollComplete(false);
      const updatedFeed = await putAdminFeed({ url: feedUrl.trim() });
      setFeed(updatedFeed);

      // Start polling for feed status
      setUrlPolling(true);
      await pollFeedStatus(updatedFeed.id);
    } catch (e) {
      console.error('Failed to update URL:', e);
    } finally {
      setSavingUrl(false);
    }
  };

  const handleSaveSiteUrl = async () => {
    if (!feed || savingSiteUrl) return;
    const trimmed = siteUrlInput.trim();
    if (!isHttpUrl(trimmed)) return;
    try {
      setSavingSiteUrl(true);
      const updatedFeed = await putAdminFeed({ siteUrl: trimmed });
      setFeed(updatedFeed);
      setSiteUrlInput(updatedFeed.siteUrl || '');
    } catch (e) {
      console.error('Failed to update site URL:', e);
    } finally {
      setSavingSiteUrl(false);
    }
  };

  const pollFeedStatus = async (feedId: string) => {
    const maxAttempts = 40; // 40 * 1.5s = 60 seconds max
    const pollInterval = 1500; // 1.5 seconds

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      if (attempt > 0) {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
      }

      try {
        const result = await authenticatedRequest<RestAdminFeed>(
          nonNull(appState.state.authData),
          'GET',
          'api',
          `/api/admin/feed/${feedId}`,
          false,
          { magic }
        );

        setFeed(result);

        // Check if we reached a final status
        const status = result.feedStatus;
        if (status == 'ACTIVE' || status == 'BLOCK' || status == 'BAD') {
          setUrlPolling(false);
          setUrlPollComplete(true);
          return;
        }
      } catch (error) {
        console.error('Error polling feed status:', error);
      }
    }

    // Timeout after max attempts
    setUrlPolling(false);
    setUrlPollComplete(true);
  };

  const handleReloadFeed = async () => {
    if (!feed) return;

    try {
      const result = await authenticatedRequest<RestAdminFeed>(
        nonNull(appState.state.authData),
        'GET',
        'api',
        `/api/admin/feed/${feed.id}`,
        false,
        { magic }
      );
      setFeed(result);
      setFeedUrl(result.url || '');
      setUrlPollComplete(false);
    } catch (e) {
      console.error('Failed to reload feed:', e);
    }
  };

  return (
    <>
      <AdminSecretModal onSecretVerified={setAdminSecret} />
      {adminSecret && (
        <FlowFormLeaveContext.Provider value={sidebarLeaveHandler}>
        <MainLayout
          selectedFeed="admin-feeds"
          right={({ onExpand }) => (
            <FlowForm main={true} className='full-margin'>
              {feed &&  (
            <div style={isPrivate ? { background: 'var(--gray-975)', borderRadius: '8px', padding: '4px' } : undefined}>
              <AdminHeader title={feed.title || feed.shortTitle || 'Untitled Feed'} onExpand={onExpand} />
              {isPrivate && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 16px', background: '#f8d7da', borderRadius: '6px', marginBottom: '16px', border: '1px solid #f5c6cb' }}>
                  <PrivateFeedIcon size={20} color="var(--error-color)" />
                  <span style={{ fontWeight: 600, color: '#721c24' }}>Private Feed</span>
                  <span style={{ color: '#856404', fontSize: '0.9rem' }}>— This feed belongs to a specific user and is not publicly visible.</span>
                </div>
              )}
              {isAdult && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 16px', background: '#f8d7da', borderRadius: '6px', marginBottom: '16px', border: '1px solid #f5c6cb' }}>
                  <span style={{ fontSize: '1.2em', color: '#721c24' }}>{'⚠'}</span>
                  <span style={{ fontWeight: 600, color: '#721c24' }}>Adult Content</span>
                  <span style={{ color: '#856404', fontSize: '0.9rem' }}>— This feed has been flagged as containing adult content.</span>
                </div>
              )}
              {duplicates.length > 0 && (
                <div style={{ padding: '12px 16px', background: '#fff3cd', borderRadius: '6px', marginBottom: '16px', border: '1px solid #ffc107' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '1.2em', color: '#856404' }}>{'⚠'}</span>
                    <span style={{ fontWeight: 600, color: '#856404' }}>Duplicate Site URL</span>
                    <span style={{ color: '#856404', fontSize: '0.9rem' }}>— {duplicates.length} other feed{duplicates.length == 1 ? '' : 's'} share{duplicates.length == 1 ? 's' : ''} this site URL.</span>
                  </div>
                  <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '0.9rem' }}>
                    {duplicates.map(d => (
                      <li key={d.id} style={{ marginBottom: '4px' }}>
                        <A href={`/a/admin/feed/${d.id}`} internal={true}>{d.shortTitle || d.title || d.id}</A>
                        {' '}
                        <span style={{ color: 'var(--gray-500)' }}>
                          [{d.feedStatus}{d.manuallyVerified ? ', verified' : ''}, {d.subscriberCount} sub{d.subscriberCount == 1 ? '' : 's'}]
                        </span>
                        {' — '}
                        <span style={{ color: 'var(--gray-350)', wordBreak: 'break-all' }}>{sanitizeLongText(d.url, 80)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {allDone && (
                <div style={{ padding: '40px', textAlign: 'center' }}>
                  <h2 style={{ color: 'var(--success-color)', marginBottom: '16px' }}>All Done!</h2>
                  <p style={{ color: 'var(--gray-400)', marginBottom: '24px' }}>
                    No more feeds require review.
                  </p>
                  <A href="/a/admin/feeds" internal={true}>Back to Admin Feeds</A>
                </div>
              )}
              {loading && (
                <div style={{ padding: '40px', textAlign: 'center', color: 'var(--gray-500)' }}>
                  Loading feed details...
                </div>
              )}
              {error && (
                <div style={{ padding: '40px', textAlign: 'center', color: 'var(--error-color)' }}>
                  Error loading feed details!
                </div>
              )}
              {allDone || <>
              {!siteUrlValid && (
                <div style={{ padding: '12px 16px', background: '#f8d7da', borderRadius: '6px', marginBottom: '16px', border: '1px solid #f5c6cb', color: '#721c24' }}>
                  <span style={{ fontWeight: 600 }}>Invalid site URL.</span> The site URL must start with http:// or https:// before this feed can be accepted or cataloged. Fix it in the URLs section below.
                </div>
              )}
              <ButtonBar size="small">
                <TextButton
                  label="Back"
                  icon={chevronBackIcon}
                  onClick={handleBack}
                  focussable="flow"
                />
                <TextButton
                  label="Edit Catalog"
                  icon={editIcon}
                  onClick={handleEditCatalog}
                  focussable="flow"
                  enabled={siteUrlValid ? 'enabled' : 'disabled'}
                />
                <TextButton
                  label="Approve"
                  icon={thumbsUpIcon}
                  onClick={() => handleSetStatus('ACTIVE')}
                  focussable="flow"
                  type='major'
                  busy={processing}
                  enabled={feed.feedStatus != 'ACTIVE' && siteUrlValid ? 'enabled' : 'disabled'}
                />
                <TextButton
                  label="Block"
                  icon={thumbsDownIcon}
                  onClick={() => handleSetStatus('BLOCK')}
                  focussable="sideway"
                  busy={processing}
                  enabled={feed.feedStatus != 'BLOCK' ? 'enabled' : 'disabled'}
                />
                <TextButton
                  label="Disable"
                  icon={closeCircleIcon}
                  onClick={() => handleSetStatus('BAD')}
                  focussable="sideway"
                  busy={processing}
                  enabled={feed.feedStatus != 'BAD' ? 'enabled' : 'disabled'}
                />
                <TextButton
                  label="Repair"
                  icon={buildIcon}
                  onClick={handleRepair}
                  focussable="flow"
                />
                <TextButton
                  label="Find Creator"
                  icon={searchIcon}
                  onClick={() => goTo(routerCtx, `/a/admin/users?feedCreator=${encodeURIComponent(feed.id)}`)}
                  focussable="flow"
                />
                {!feed.postCount && !feed.nextFetchAt && (
                  <TextButton
                    label="Reprocess"
                    icon={refreshIcon}
                    onClick={handleReprocess}
                    focussable="flow"
                    busy={reprocessing}
                  />
                )}
                <TextButton
                  label="Reset"
                  icon={refreshIcon}
                  onClick={handleResetFeed}
                  focussable="flow"
                  type="minor"
                  busy={resetting}
                />
                {!!(feed.errorType || feed.fetchErrorCount) && (
                  <TextButton
                    label="Debug RSS"
                    icon={bugIcon}
                    onClick={() => goTo(routerCtx, `/a/admin/feed/debug?url=${encodeURIComponent(feed.url || '')}&feedId=${encodeURIComponent(feed.id)}`)}
                    focussable="flow"
                  />
                )}
                {canDeleteFeed && (
                  <TextButton
                    label="Delete"
                    icon={trashIcon}
                    onClick={handleDeleteFeed}
                    focussable="flow"
                    type="minor"
                    busy={deleting}
                  />
                )}
              </ButtonBar>

              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                border: '1px solid var(--gray-850)',
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}>
                <tbody>
                  {renderSectionHeader('Basic Information')}
                  {renderRow('ID (friendly)', feed.friendlyId ? `${feed.id} (${feed.friendlyId})` : feed.id)}
                  {renderRow('Feed Title', feed.title)}
                  {renderShortTitleRow()}
                  {renderRow('Feed Description', feed.description)}
                  {renderImageEditorRow()}
                  {(() => {
                    const websiteUrl = feed.siteUrl || feed.url;
                    if (!websiteUrl) return null;
                    return (
                      <tr style={{ borderBottom: '1px solid var(--gray-925)' }}>
                        <td style={{ padding: '12px 16px', fontWeight: 500, verticalAlign: 'top', width: '220px', color: 'var(--gray-350)', backgroundColor: headerBg }}>Website</td>
                        <td style={{ padding: '12px 16px', wordBreak: 'break-word', color: 'var(--gray-200)' }}>
                          <a href={websiteUrl} target="_blank" rel="noopener noreferrer">{sanitizeLongText(websiteUrl, 100)}</a>
                        </td>
                      </tr>
                    );
                  })()}
                  {renderRow('Category', feed.category)}
                  {renderRow('Type', feed.type)}
                  {renderRow('Source Format', feed.sourceFormat == 'rss' ? 'RSS' : feed.sourceFormat == 'atom' ? 'Atom' : feed.sourceFormat == 'json' ? 'JSON' : undefined)}
                  {renderRow('Tag', feed.tag)}
                  {(() => {
                    if (!feed.lastNewPostPubDate) return null;
                    const date = new Date(feed.lastNewPostPubDate);
                    const formatted = formatCompactDate(feed.lastNewPostPubDate);
                    const localTime = date.toLocaleString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
                    const stale = (Date.now() - date.getTime()) > 365 * 24 * 60 * 60 * 1000;
                    const text = `${formatted} (${localTime})`;
                    return (
                      <tr style={{ borderBottom: '1px solid var(--gray-925)' }}>
                        <td style={{ padding: '12px 16px', fontWeight: 500, verticalAlign: 'top', width: '220px', color: 'var(--gray-350)', backgroundColor: headerBg }}>Last New Post Pub Date</td>
                        <td style={{ padding: '12px 16px', wordBreak: 'break-word', color: stale ? 'var(--error-color)' : 'var(--gray-200)', fontWeight: stale ? 700 : undefined }}>{text}</td>
                      </tr>
                    );
                  })()}

                  {renderSectionHeader('Status')}
                  {renderFeedStatusRow()}
                  {renderRow('Error Type', feed.errorType)}
                  {renderRow('Fetch Error Count', feed.fetchErrorCount)}
                  {renderDateRow('Failing Since', feed.fetchFailingSinceAt)}
                  {renderLastFetchErrorRow()}
                  {renderRow('Paid/Free', feed.paid === true ? 'Paid' : feed.paid === false ? 'Free' : 'Unknown')}
                  {feed.proxyName ? (
                    <tr style={{ borderBottom: '1px solid var(--gray-925)' }}>
                      <td style={{ padding: '12px 16px', fontWeight: 500, verticalAlign: 'top', width: '220px', color: 'var(--gray-350)', backgroundColor: headerBg }}>Proxy</td>
                      <td style={{ padding: '12px 16px', wordBreak: 'break-word', color: 'var(--gray-200)' }}>
                        {feed.proxyName}
                        {feed.proxyFirstUsedAt && ` (since ${formatCompactDate(feed.proxyFirstUsedAt)})`}
                      </td>
                    </tr>
                  ): <tr style={{ borderBottom: '1px solid var(--gray-925)' }}>
                      <td style={{ padding: '12px 16px', fontWeight: 500, verticalAlign: 'top', width: '220px', color: 'var(--gray-350)', backgroundColor: headerBg }}>Proxy</td>
                      <td style={{ padding: '12px 16px', wordBreak: 'break-word', color: 'var(--gray-200)' }}>
                        None
                      </td>
                    </tr>}
                  {renderVerificationRow()}
                  {renderDateRow('Last Verified At', feed.lastVerifiedAt)}
                  {renderRow('Verified By User ID', feed.verifiedByUserId)}

                  {renderSectionHeader('URLs')}
                  {renderRssUrlRow()}
                  {renderSiteUrlRow()}
                  {feed.aliases?.length ? (
                    <tr style={{ borderBottom: '1px solid var(--gray-925)' }}>
                      <td style={{ padding: '12px 16px', fontWeight: 500, verticalAlign: 'top', width: '220px', color: 'var(--gray-350)', backgroundColor: headerBg }}>
                        Aliases ({feed.aliases.length})
                      </td>
                      <td style={{ padding: '12px 16px', wordBreak: 'break-word', color: 'var(--gray-200)' }}>
                        {feed.aliases.map(alias => {
                          const mismatch = aliasHostMismatch(alias, feed.siteUrl);
                          return (
                            <div key={alias} style={{ marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              <A href={/^https?:\/\//.test(alias) ? alias : `https://${alias}`}>{alias}</A>
                              {mismatch && <span title="Alias host differs from the site URL host" style={{ color: 'var(--error-color)', fontWeight: 600 }}>⚠ host mismatch</span>}
                              <TextButton
                                label="Remove"
                                icon={closeCircleIcon}
                                onClick={() => handleDeleteAlias(alias, false)}
                                focussable="flow"
                                type="minor"
                                size="small"
                                busy={deletingAlias == alias}
                              />
                            </div>
                          );
                        })}
                      </td>
                    </tr>
                  ) : null}
                  {feed.pendingAliases?.length ? (
                    <tr style={{ borderBottom: '1px solid var(--gray-925)' }}>
                      <td style={{ padding: '12px 16px', fontWeight: 500, verticalAlign: 'top', width: '220px', color: '#856404', backgroundColor: headerBg }}>
                        Pending aliases ({feed.pendingAliases.length})
                        <div style={{ fontWeight: 400, fontSize: '0.8rem', color: '#856404' }}>Not active until verified</div>
                      </td>
                      <td style={{ padding: '12px 16px', wordBreak: 'break-word', color: 'var(--gray-200)' }}>
                        {feed.pendingAliases.map(alias => {
                          const mismatch = aliasHostMismatch(alias, feed.siteUrl);
                          return (
                            <div key={alias} style={{ marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              {alias}
                              {mismatch && <span title="Pending alias host differs from the site URL host" style={{ color: 'var(--error-color)', fontWeight: 600 }}>⚠ host mismatch</span>}
                              <TextButton
                                label="Remove"
                                icon={closeCircleIcon}
                                onClick={() => handleDeleteAlias(alias, true)}
                                focussable="flow"
                                type="minor"
                                size="small"
                                busy={deletingAlias == alias}
                              />
                            </div>
                          );
                        })}
                      </td>
                    </tr>
                  ) : null}
                  {feed.aliasConflicts?.length ? (
                    <tr style={{ borderBottom: '1px solid var(--gray-925)' }}>
                      <td style={{ padding: '12px 16px', fontWeight: 500, verticalAlign: 'top', width: '220px', color: 'var(--error-color)', backgroundColor: headerBg }}>
                        Alias conflicts ({feed.aliasConflicts.length})
                      </td>
                      <td style={{ padding: '12px 16px', wordBreak: 'break-word', color: 'var(--gray-200)' }}>
                        {feed.aliasConflicts.map(c => (
                          <div key={c.aliasUrl} style={{ marginBottom: '4px' }}>
                            {c.aliasUrl} — already owned by <A href={`/a/admin/feed/${c.ownedByFeedId}`} internal={true}>{c.ownedByFeedId}</A>
                          </div>
                        ))}
                      </td>
                    </tr>
                  ) : null}
                  {renderAddAliasRow()}

                  {renderSectionHeader('Statistics')}
                  {renderRow('Subscriber Count', feed.subscriberCount)}
                  {renderRow('Post Count', feed.postCount)}
                  {renderMaxStoredPostsRow()}
                  {renderRow('First Archived Post Index', feed.firstArchivedPostIndex)}
                  {renderRow('Fetch Interval Mins', feed.fetchIntervalMins)}
                  {feed.postFrequency && renderRow('Post Frequency', `${formatFrequency(feed.postFrequency)} (${feed.postFrequency.toFixed(2)}/day)`)}

                  {renderSectionHeader('Timestamps')}
                  {renderDateRow('Created At', feed.createdAt)}
                  {renderDateRow('Last Config Change At', feed.lastConfigChangeAt)}
                  {renderDateRow('Last Fetched At', feed.lastFetchedAt)}
                  {renderDateRow('Next Fetch At', feed.nextFetchAt)}

                  {renderSectionHeader('WebSub')}
                  {renderRow('Hub URL', feed.webSubHubUrl)}
                  {renderDateRow('Next Renew', feed.webSubNextRenew)}
                  {renderDateRow('Subscribed At', feed.webSubSubscribedAt)}
                  {renderDateRow('Last Verify', feed.webSubLastVerify)}
                  {renderDateRow('Last Notify', feed.webSubLastNotify)}
                  {renderRow('Failures', feed.webSubFailures)}
                  {renderDateRow('Retry At', feed.webSubRetryAt)}
                  {renderDateRow('Broken Until', feed.webSubBrokenUntil)}

                  {renderSectionHeader('Additional Info')}
                  {renderRow('Copyright', feed.copyright)}
                  {renderCommentRow(
                    'Public Comment',
                    publicComment,
                    feed.downreadPublicComment,
                    setPublicComment,
                    handleSavePublicComment,
                    savingPublicComment,
                    publicCommentConfirmPending
                  )}
                  {renderCommentRow(
                    'Private Comment',
                    privateComment,
                    feed.downreadPrivateComment,
                    setPrivateComment,
                    handleSavePrivateComment,
                    savingPrivateComment
                  )}
                </tbody>
              </table>

              <FeedPreview feed={feed} maxPosts={20} showPostNumber />
            </>}
            </div>
          )}
            </FlowForm>
          )}
          startWithLeftCollapsed={false}
        />
        </FlowFormLeaveContext.Provider>
      )}
    </>
  );
};

export default AdminViewFeedPage;
